import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { pageHead, jsonLd } from "@/lib/seo";

export const Route = createFileRoute("/faq")({
  head: () => ({
    ...pageHead({
      path: "/faq",
      title: "Frequently asked questions — Book Swap",
      description: "How swaps, loans and donations work on Book Swap, plus safety, meeting neighbors, returns and what happens to damaged books.",
    }),
    scripts: [
      jsonLd({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: qas.map((qa) => ({
          "@type": "Question",
          name: qa.en.q,
          acceptedAnswer: { "@type": "Answer", text: qa.en.a },
        })),
      }),
    ],
  }),
  component: Faq,
});

type QA = { en: { q: string; a: string }; ar: { q: string; a: string } };

const qas: QA[] = [
  {
    en: { q: "How do I arrange a swap?", a: "Browse books near you, message the owner from the listing page, and agree on a time and public meeting point. The chat thread keeps a record so both sides can follow up." },
    ar: { q: "كيف أرتّب عملية تبادل؟", a: "تصفح الكتب القريبة منك، راسل المالك من صفحة الإعلان، واتفقا على وقت ومكان عام للقاء. تبقى المحادثة محفوظة ليرجع إليها الطرفان." },
  },
  {
    en: { q: "Is my home address shown publicly?", a: "No. We only show your neighborhood and an approximate distance. Your exact coordinates are rounded before storage and never shared with other readers." },
    ar: { q: "هل يظهر عنوان منزلي للعامة؟", a: "لا. نعرض الحي فقط والمسافة التقريبية. يتم تقريب إحداثياتك قبل التخزين ولا تتم مشاركتها مع القراء الآخرين." },
  },
  {
    en: { q: "What if someone doesn't return a book?", a: "You can report the listing or the borrower from their profile. Repeated reports add strikes to the account, and three strikes permanently close it." },
    ar: { q: "ماذا لو لم يُعِد أحدهم كتابي؟", a: "يمكنك الإبلاغ عن الإعلان أو المستعير من صفحته. تُضاف مخالفات للحساب عند تكرار البلاغات، وثلاث مخالفات تُغلق الحساب نهائيًا." },
  },
  {
    en: { q: "Can I list books as free donations?", a: "Yes. Toggle the donation switch when listing a book. Donations don't require a swap in return — pure pay-it-forward." },
    ar: { q: "هل يمكنني عرض كتب كهدايا مجانية؟", a: "نعم. فعّل خيار التبرع عند إدراج الكتاب. التبرعات لا تتطلب تبادلًا — فقط عطاء خالص." },
  },
  {
    en: { q: "How do I stay safe at a meet-up?", a: "Always meet in a busy public place during daylight, like a coffee shop or library. Tell a friend where you're going. Trust your instincts and cancel if anything feels off." },
    ar: { q: "كيف أبقى آمنًا أثناء اللقاء؟", a: "التقِ دائمًا في مكان عام مزدحم خلال النهار مثل مقهى أو مكتبة. أخبر صديقًا بمكان وجودك. ثق بحدسك وألغِ اللقاء إن شعرت بأي قلق." },
  },
  {
    en: { q: "Is Book Swap available in my city?", a: "Book Swap works anywhere. The community is strongest where there are more readers — invite your neighbors to grow your local shelf." },
    ar: { q: "هل Book Swap متاح في مدينتي؟", a: "يعمل Book Swap في أي مكان. المجتمع أقوى حيث يوجد قرّاء أكثر — ادعُ جيرانك لتوسيع مكتبة حيّك." },
  },
  {
    en: { q: "Does it cost anything?", a: "No. Book Swap is 100% free — no fees, no shipping, no subscriptions. Ever." },
    ar: { q: "هل يكلّف أي شيء؟", a: "لا. Book Swap مجاني بالكامل — بلا رسوم، بلا شحن، بلا اشتراكات أبدًا." },
  },
];

function Faq() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-12" dir={isAr ? "rtl" : "ltr"}>
        <h1 className="font-serif text-3xl md:text-4xl">{isAr ? "الأسئلة الشائعة" : "Frequently asked questions"}</h1>
        <p className="mt-3 text-muted-foreground">
          {isAr ? "إجابات سريعة عن كيفية عمل Book Swap والسلامة وعمليات التبادل." : "Quick answers about how Book Swap works, safety, and swaps."}
        </p>

        <Accordion type="single" collapsible className="mt-8 rounded-2xl bg-card p-2 shadow-[var(--shadow-card)]">
          {qas.map((qa, i) => {
            const node = isAr ? qa.ar : qa.en;
            return (
              <AccordionItem key={i} value={`item-${i}`} className="border-border px-4">
                <AccordionTrigger className="text-left font-semibold">{node.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{node.a}</AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        <div className="mt-10 rounded-2xl bg-primary-soft p-6 text-center">
          <p className="font-semibold">{isAr ? "لم تجد إجابتك؟" : "Didn't find your answer?"}</p>
          <p className="mt-1 text-sm text-muted-foreground">{isAr ? "تواصل معنا وسنرد خلال يوم عمل." : "Reach out and we'll get back within a business day."}</p>
          <Link to="/contact" className="mt-4 inline-block">
            <Button className="bg-primary hover:bg-primary/90">{isAr ? "تواصل معنا" : "Contact us"}</Button>
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
